import {StoreState, AppDispatch, Thunk} from '~/frontend/src/store';
import {isDataModified} from '~/frontend/src/utilities/dataUtils';
import startRequest from '~/frontend/src/actions/ajax/startRequest';
import endRequest from '~/frontend/src/actions/ajax/endRequest';
import {waitForPendingValueChanges} from '~/frontend/src/utilities/pendingValueChanges';


/**
 * @memberOf! Actions
 *
 * @param {string} panelKey
 * @param {string} dataContext
 * @param {Schema} schema
 *
 */
const deferedInputValueUpdate = ( panelKey : string, dataContext : string, schema : Schema ) : Thunk<Promise<void>> => async ( dispatch : AppDispatch, getState : () => StoreState ) : Promise<void> => {
    if ( document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA' ) {
        const activeElement = document.activeElement as HTMLElement;

        // show loading mask while updating
        dispatch( startRequest(
            () => {
            },
            panelKey,
            'processing'
        ) );

        try {
            // make sure loading mask is displayed as soon as possible
            await new Promise( resolve => {
                setTimeout( () => {
                    resolve( true );
                }, 1 );
            } );

            // invoke blur on active element to update value
            await new Promise( resolve => {
                const maxParentCount = 5;

                // cell editor?
                let element : HTMLElement | null = activeElement,
                    parentCount                  = 0;

                while ( element && element.tagName !== 'TD' && parentCount++ < maxParentCount ) {
                    element = element.parentElement;
                }

                // cell editor?
                if ( element?.classList.contains( 'p-cell-editing' ) ) {
                    element.addEventListener( 'keydown', () => {
                        let count = 0;

                        const intervalHandle = setInterval( () => {
                            if ( document.activeElement === document.body || count++ > 20 ) {
                                clearInterval( intervalHandle );
                                setTimeout( () => {
                                    resolve( true );
                                }, 1 );
                            }
                        }, 1 );
                    }, { once: true } );

                    element.dispatchEvent( new KeyboardEvent( 'keydown', { which: 13, keyCode: 13, bubbles: true, cancelable: true } ) );
                }
                else {
                    activeElement.addEventListener( 'blur', () => {
                        resolve( true );
                    }, { once: true } );

                    activeElement.blur();
                }
            } );

            // the blur above triggers Form.onValueChange, which commits the value to the
            // model asynchronously (onValueChangeAsync). Wait for that commit to finish so
            // the downstream isDataModified check sees the value - matches the panel itself
            // and all of its subpanels (e.g. detail forms in a TreeView).
            await waitForPendingValueChanges( componentUid =>
                componentUid === panelKey
                || componentUid === dataContext
                || componentUid.startsWith( panelKey + '_' )
                || componentUid.startsWith( panelKey + '-' )
                || componentUid.startsWith( dataContext + '_' )
                || componentUid.startsWith( dataContext + '-' )
            );
        }
        finally {
            // remove loading mask while updating; always end, even if the above threw
            dispatch( endRequest(
                () => {
                },
                panelKey,
                'processing'
            ) );
        }

        // make sure loading mask is hidden as soon as possible,
        // but only if there is no data to be saved (otherwise loading mask will be hidden after save)
        if ( !isDataModified( dataContext, schema ) ) {
            await new Promise( resolve => {
                setTimeout( () => {
                    resolve( true );
                }, 1 );
            } );
        }
    }
}
export type DeferedInputValueUpdate = typeof deferedInputValueUpdate;

export default deferedInputValueUpdate;
